import React, { useState } from "react";
import { MapPin, Search } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { useSEO } from "@/hooks/useSEO";
import { PageHero } from "@/components/shared/PageHero";
import { PageCta } from "@/components/shared/PageCta";
import { Section } from "@/components/ui-kit/Kit";
import { Reveal, Stagger, StaggerItem } from "@/components/motion/Reveal";

export default function Districts() {
  const { t } = useI18n();
  const p = t("pages.districts");
  const [q, setQ] = useState("");
  useSEO({ title: p.title, description: p.lead, path: "/districts" });

  const list = (p.list || []).filter((d) => d.toLowerCase().includes(q.toLowerCase()));

  return (
    <>
      <PageHero eyebrow={p.eyebrow} title={p.title} lead={p.lead} />
      <Section className="py-20 sm:py-24" data-testid="districts-page">
        <div className="flex flex-col gap-6 border-b border-navy/10 pb-8 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-navy/35" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="district-search"
              aria-label="Search districts"
              placeholder="Search district"
              className="w-full border border-navy/15 bg-white py-3 pl-11 pr-4 text-sm text-navy outline-none transition-colors duration-200 focus:border-grn"
            />
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {(p.fields || []).map((f) => (
              <span key={f} className="font-mono text-[10px] uppercase tracking-[0.18em] text-navy/40">
                {f}
              </span>
            ))}
          </div>
        </div>

        <Stagger className="mt-10 grid gap-px border border-navy/10 bg-navy/10 sm:grid-cols-2 lg:grid-cols-4" step={0.02}>
          {list.map((d) => (
            <StaggerItem key={d} className="bg-white">
              <div className="group flex items-center gap-3 p-5 transition-colors duration-300 hover:bg-navy sm:p-6" data-testid={`district-${d}`}>
                <MapPin className="h-4 w-4 shrink-0 text-grn transition-colors duration-300 group-hover:text-gold" strokeWidth={1.6} />
                <span className="font-display text-[15px] font-medium text-navy transition-colors duration-300 group-hover:text-white">
                  {d}
                </span>
              </div>
            </StaggerItem>
          ))}
        </Stagger>

        {list.length === 0 && (
          <div
            className="mt-10 border border-dashed border-navy/20 bg-slate-50/60 p-8"
            data-testid="district-no-results"
          >
            <p className="text-[13px] text-slate-600">
              No district matches “{q}”. GPU currently covers {(p.list || []).length} Gujarat districts.
            </p>
          </div>
        )}

        <Reveal>
          <p className="mt-10 max-w-3xl border-l-2 border-gold pl-5 text-[12px] leading-relaxed text-slate-500">
            {t("common.noContentYet")}
          </p>
        </Reveal>
      </Section>
      <PageCta />
    </>
  );
}
